import { readingSortKey } from "../content/cidian/cidian-reading-key.js";
import { canonicalAddress } from "../segmenting/canonical-entry.js";
import { mapLimited } from "./map-limited.js";
import { record, strings } from "./sheet-data.js";
import { readingsOf, type SheetReading } from "./worksheet-entries.js";

/**
 * A word as a revision booklet prints it, read from its `canonical.json`.
 *
 * The 词典 publishes the object beside every entry, and since #1068 it names
 * the word's 繁體 writing, its meanings reading by reading, its everyday
 * classifiers and the 例句 it is shown by, each where the entry has one. See
 * `src/cidian-page/cidian-canonical-object.ts`. A booklet asks for the words
 * it prints and no others, one object each, the way the worksheets do.
 */

/** One reading and the meanings under it. */
export interface BookletReading {
  /** The syllables and their tones, for colouring. */
  readonly said: SheetReading;

  /** The reading as plain text. */
  readonly pinyin: string;

  readonly meanings: readonly string[];
}

/** One everyday classifier with a phrase counting the word. */
export interface BookletClassifier {
  readonly liangci: string;
  readonly pinyin: string;
  readonly phrase: string;
  readonly english: string;
}

/** One word, as much of its entry as a booklet prints. */
export interface BookletEntry {
  /** The headword, as the 词典 files it. */
  readonly word: string;

  /** The 繁體 writing, where it differs. */
  readonly fanti: string | undefined;

  /** Its readings, each with its own meanings. */
  readonly readings: readonly BookletReading[];

  readonly classifiers: readonly BookletClassifier[];

  /** The uuid of the 例句 it is shown by, where the library has one. */
  readonly liju: string | undefined;

  /** Whether the word has a `/ci/` page of sentences and grammar articles. */
  readonly ci: boolean;

  /** The HSK band, where the word list grades it. */
  readonly band: string | undefined;

  /** What it files under in a list arranged by how words sound. */
  readonly sortKey: string;
}

/** A classifier as the object writes it, read defensively. */
function classifierOf(value: unknown): BookletClassifier | undefined {
  const fields = record(value);
  const liangci = fields?.["liangci"];
  const pinyin = fields?.["pinyin"];
  const phrase = fields?.["phrase"];
  const english = fields?.["english"];

  return typeof liangci === "string" &&
    typeof pinyin === "string" &&
    typeof phrase === "string" &&
    typeof english === "string" &&
    liangci !== ""
    ? { liangci, pinyin, phrase, english }
    : undefined;
}

/** A reading as the object writes it, read defensively. */
function readingOf(value: unknown): BookletReading | undefined {
  const fields = record(value);
  const pinyin = fields?.["pinyin"];
  const reading = fields?.["reading"];
  const meanings = strings(fields?.["meanings"]);
  const said = typeof reading === "string" ? readingsOf(reading)[0] : undefined;

  return typeof pinyin === "string" && meanings !== undefined
    ? {
        said: said ?? { syllables: [pinyin], tones: [5] },
        pinyin,
        meanings,
      }
    : undefined;
}

/**
 * Every reading the markup holds as one, separated as the 词典 separates
 * them. An object with no `readings` cannot say which meanings go with which
 * reading, so it keeps them together and loses none of the pronunciations.
 */
function joined(markup: string, pinyin: string): SheetReading {
  const readings = readingsOf(markup);
  if (readings.length === 0) {
    return { syllables: [pinyin], tones: [5] };
  }

  return {
    syllables: readings.flatMap((one, at) =>
      at === 0 ? one.syllables : [", ", ...one.syllables],
    ),
    tones: readings.flatMap((one, at) =>
      at === 0 ? one.tones : [5, ...one.tones],
    ),
  };
}

/**
 * A word's object, read defensively, since it arrives over the network.
 *
 * An object written before #1068, or one an entry with a single reading
 * writes, has no `readings`. The joined reading and every meaning stand in
 * as its one reading.
 */
export function bookletEntry(body: unknown): BookletEntry | undefined {
  const fields = record(body);
  const word = fields?.["word"];
  const pinyin = fields?.["pinyin"];
  const reading = fields?.["reading"];
  const meanings =
    strings(fields?.["meaningsShown"]) ?? strings(fields?.["meanings"]);
  if (
    typeof word !== "string" ||
    word === "" ||
    typeof pinyin !== "string" ||
    meanings === undefined
  ) {
    return undefined;
  }
  const markup = typeof reading === "string" ? reading : "";
  const listed = Array.isArray(fields?.["readings"])
    ? (fields["readings"] as unknown[]).flatMap((value) => {
        const one = readingOf(value);

        return one === undefined ? [] : [one];
      })
    : [];
  const readings =
    listed.length > 0
      ? listed
      : [{ said: joined(markup, pinyin), pinyin, meanings }];
  const fanti = fields?.["fanti"];
  const liju = fields?.["liju"];
  const hsk = fields?.["hsk"];
  const first = markup.split(",")[0] ?? "";

  return {
    word,
    fanti: typeof fanti === "string" && fanti !== "" ? fanti : undefined,
    readings,
    classifiers: Array.isArray(fields?.["classifiers"])
      ? (fields["classifiers"] as unknown[]).flatMap((value) => {
          const one = classifierOf(value);

          return one === undefined ? [] : [one];
        })
      : [],
    liju: typeof liju === "string" && liju !== "" ? liju : undefined,
    ci: fields?.["ci"] === true,
    band: typeof hsk === "string" && hsk !== "" ? hsk : undefined,
    sortKey: `${readingSortKey({
      pinyin: pinyin.split(",")[0]?.trim() ?? "",
      pinyinHtml: first === "" ? undefined : first,
    })}\u0000${word}`,
  };
}

/** What reading the words needs. */
export interface BookletEntriesProperties {
  readonly fetch: typeof fetch;

  /** How many requests are in flight at once. */
  readonly concurrency: number;
}

/** Reads the words of a booklet, each once a page. */
export class BookletEntries {
  readonly #fetch: typeof fetch;
  readonly #concurrency: number;
  readonly #held = new Map<string, Promise<BookletEntry | undefined>>();

  constructor(properties: BookletEntriesProperties) {
    this.#fetch = properties.fetch;
    this.#concurrency = properties.concurrency;
  }

  /**
   * These words, leaving out any the 词典 does not publish or that could not
   * be read, in the order asked for.
   */
  async some(words: readonly string[]): Promise<readonly BookletEntry[]> {
    const found = await mapLimited(words, this.#concurrency, (word) =>
      this.#one(word),
    );

    return found.filter((entry) => entry !== undefined);
  }

  #one(word: string): Promise<BookletEntry | undefined> {
    let held = this.#held.get(word);
    if (held === undefined) {
      held = this.#read(word);
      this.#held.set(word, held);
      // A failure is forgotten, so the next draw asks again.
      void held.then((entry) => {
        if (entry === undefined) {
          this.#held.delete(word);
        }
      });
    }

    return held;
  }

  async #read(word: string): Promise<BookletEntry | undefined> {
    try {
      const response = await this.#fetch(canonicalAddress(word));

      return response.ok ? bookletEntry(await response.json()) : undefined;
    } catch {
      return undefined;
    }
  }
}
